import { useEffect, useRef, type PointerEvent, type RefObject } from 'react';
import { Minus, SquarePen } from 'lucide-react';
import { IconButton } from '@/components/ui';
import { Wordmark } from '@/components/brand';
import { Composer, type ChatImage } from '@/components/chat';
import { ShibaMessageView, type ShibaMessage } from './ShibaMessage';

/** Stable target of `aria-controls` on both halves of the disclosure. */
export const SHIBA_PANEL_ID = 'shiba-panel';

export interface ShibaPanelProps {
  messages: ShibaMessage[];
  /** A question is streaming — block a second send meanwhile. */
  sending: boolean;
  /** The user just opened the panel (and only then): give them the cursor. */
  focusOnMount: boolean;
  /**
   * O compositor mora aqui, mas quem zera a conversa é o widget — e recomeçar só
   * está terminado quando o cursor volta ao campo. A referência sobe para lá.
   */
  composerRef: RefObject<HTMLTextAreaElement>;
  onSend: (question: string, image?: ChatImage) => void;
  /** Zera a conversa da sessão: sem confirmação, sempre disponível. */
  onNewConversation: () => void;
  onRetry: (message: ShibaMessage) => void;
  onClose: () => void;
  /** Starts a frame drag: the header doubles as the panel handle. */
  onGrab: (event: PointerEvent) => void;
}

/**
 * Expanded state of the Shiba widget: the conversation panel. A draggable header
 * handle (logo + "Online" with its green dot + the new-conversation/collapse
 * pair), a polite live log of messages, the shared {@link Composer} (image
 * attach, microphone and send inside the box; Enter sends, Shift+Enter breaks
 * the line) and the external-model disclaimer.
 * It is a NON-modal dialog: the page underneath stays alive and the focus is
 * never contained. Open/close state and the conversation itself live in
 * {@link ShibaWidget}.
 */
export function ShibaPanel({
  messages,
  sending,
  focusOnMount,
  composerRef: inputRef,
  onSend,
  onNewConversation,
  onRetry,
  onClose,
  onGrab,
}: ShibaPanelProps): JSX.Element {
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Um chat existe para escrever: abrir entrega o cursor ao compositor. Só quando
    // foi o usuário que abriu — o painel restaurado da preferência salva aparece
    // sem roubar o foco de quem acabou de carregar a página (WCAG 3.2.1).
    if (focusOnMount) inputRef.current?.focus();
  }, [focusOnMount]);

  useEffect(() => {
    // Keep the latest message in view as the conversation grows (guarded for jsdom).
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [messages]);

  return (
    <section
      id={SHIBA_PANEL_ID}
      className="shiba-panel"
      role="dialog"
      aria-modal="false"
      aria-label="Chat Shiba"
    >
      <header className="shiba-panel__header shiba__grip" onPointerDown={onGrab}>
        {/* A MESMA marca da pílula, maior: o painel é o estado aberto dela, e uma
            marca que troca na passagem pílula ⇄ painel pisca no quadro em que o usuário
            está olhando. O wordmark já escreve "Shiba", então não há título ao
            lado; por isso a marca tem rótulo (aqui o logo É o nome). O estado fica
            no pé do logo: "Online" em texto, com o ponto verde de volta (pedido do
            usuário na Onda AD) como pintura do CSS — quem diz o estado é a palavra. */}
        <div className="shiba-panel__identity">
          <Wordmark size="h40" label="Shiba" className="shiba__logo" />
          <p className="shiba-panel__status shiba-panel__status--online">Online</p>
        </div>
        {/* Os dois controles do cabeçalho andam juntos e com folga menor entre si
            que a do resto da linha: são um par, não dois itens soltos. Recomeçar
            vem antes de recolher — a ordem em que a pessoa decide. */}
        <div className="shiba-panel__actions">
          <IconButton
            size="sm"
            label="Nova conversa"
            icon={<SquarePen />}
            // Reaching for the control must not drag the panel away.
            onPointerDown={(event) => event.stopPropagation()}
            onClick={onNewConversation}
          />
          <IconButton
            size="sm"
            label="Recolher o agente"
            icon={<Minus />}
            // A outra metade da divulgação: o launcher desmonta ao abrir, então é
            // este controle que carrega o estado expandido do MESMO painel.
            aria-expanded="true"
            aria-controls={SHIBA_PANEL_ID}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={onClose}
          />
        </div>
      </header>

      <div
        className="shiba-panel__log"
        role="log"
        aria-live="polite"
        aria-label="Conversa com a Shiba"
      >
        {messages.map((message) => (
          <ShibaMessageView key={message.id} message={message} onRetry={onRetry} />
        ))}
        <div ref={endRef} aria-hidden="true" />
      </div>

      {/* O MESMO compositor do Chat de Conhecimento: anexo, microfone e envio
          dentro da caixa. Aqui Enter envia (a pergunta do widget é curta). */}
      <div className="shiba-panel__dock">
        <Composer
          compact
          inputRef={inputRef}
          inputLabel="Mensagem para a Shiba"
          placeholder="Pergunte à Shiba"
          sendLabel="Enviar mensagem"
          sendOn="enter"
          onSend={onSend}
          pending={sending}
        />
      </div>

      <p className="shiba-panel__disclaimer">
        As mensagens desta conversa são enviadas a um provedor de modelo externo. Não compartilhe
        dados pessoais ou confidenciais.
      </p>
    </section>
  );
}
